import { useState } from "react";
import { STATUS_LABEL } from "../hooks/useVertragstext";

/**
 * Kopf einer Fassung: Prüfen, Freigeben, Verwerfen — und Speichern.
 *
 * Vertrag und AGB sind **eine** Fassung. Wer auf der AGB-Seite freigibt, gibt
 * auch den Vertragstext derselben Fassung frei und umgekehrt: die Urkunde
 * verweist auf Paragrafen der AGB, und ein Vertrag muss beide aus demselben
 * Stand bekommen. Deshalb steht dieser Kopf auf beiden Seiten gleich.
 */
export default function FassungKopf({
  status, bearbeitbar, version, vertraege, onPruefen, pruefErgebnis, onFreigeben, onVerwerfen,
  amArbeiten, fehler, offen, speichertGerade, onSpeichern,
}: {
  status: string; bearbeitbar: boolean; version: number; vertraege: number;
  /** Wie viele Felder ungespeichert sind — für den Knopf „Speichern". */
  offen: number;
  speichertGerade: boolean;
  onSpeichern: () => void;
  onPruefen: () => void;
  pruefErgebnis?: { in_ordnung: boolean; fehler: string[] };
  onFreigeben: (giltAb: string) => void;
  onVerwerfen: () => void;
  amArbeiten: boolean;
  fehler?: string;
}) {
  const [giltAb, setGiltAb] = useState(() => new Date().toISOString().slice(0, 10));

  // In Kraft, aber noch niemand hat zugestimmt: der Text lässt sich direkt
  // ändern (Entwicklungsphase, 28.09.2026). Jede Änderung gilt sofort für alle
  // offenen Angebote — deshalb steht das in Rot über dem Knopf.
  if (status === "freigegeben" && bearbeitbar) {
    return (
      <div className="pf-card vt-kopf">
        <div className="vt-kopf-zeile">
          <button className={"pf-btn is-small" + (offen ? "" : " is-ghost")}
                  onClick={onSpeichern} disabled={offen === 0 && !speichertGerade}>
            {offen ? `Speichern (${offen})` : speichertGerade ? "Speichere…" : "Gespeichert ✓"}
          </button>
          <button className="pf-btn is-ghost is-small" onClick={onPruefen} disabled={amArbeiten}>
            Prüfen
          </button>
        </div>
        {pruefErgebnis && (
          pruefErgebnis.in_ordnung
            ? <div className="vt-ok">Rendert in allen Varianten sauber.</div>
            : <div className="vt-fehler">
                <strong>Diese Stellen rendern nicht:</strong>
                {pruefErgebnis.fehler.map((f, i) => <div key={i}>{f}</div>)}
              </div>
        )}
        {fehler && <div className="vt-fehler">{fehler}</div>}
        <div className="vt-hint">
          <strong>V{version} ist in Kraft und noch bearbeitbar</strong>, weil ihr noch kein
          Kunde zugestimmt hat. Jede Änderung gilt sofort für alle offenen Angebote. Mit
          der ersten Zustimmung wird sie festgeschrieben — danach geht es nur noch über
          eine neue Fassung.
        </div>
      </div>
    );
  }

  if (status !== "entwurf") {
    return (
      <div className="pf-card vt-kopf is-fest">
        <div>
          <strong>V{version} ist {STATUS_LABEL[status].toLowerCase()}</strong> und wird nicht
          mehr bearbeitet.{vertraege ? ` ${vertraege} Vertrag/Verträge sind darauf festgeschrieben.` : ""}
          {" "}Für eine Änderung eine neue Fassung aus dieser anlegen.
        </div>
      </div>
    );
  }

  return (
    <div className="pf-card vt-kopf">
      <div className="vt-kopf-zeile">
        {/* Speichern geschieht auch von selbst, sobald ein Feld verlassen wird.
            Der Knopf sagt, ob noch etwas offen ist — und sichert es. */}
        <button className={"pf-btn is-small" + (offen ? "" : " is-ghost")}
                onClick={onSpeichern} disabled={offen === 0 && !speichertGerade}>
          {offen ? `Speichern (${offen})` : speichertGerade ? "Speichere…" : "Gespeichert ✓"}
        </button>
        <button className="pf-btn is-ghost is-small" onClick={onPruefen} disabled={amArbeiten}>
          Prüfen
        </button>
        <label className="vt-giltab">
          <span>gilt ab</span>
          <input className="pf-input is-tight" type="date" value={giltAb}
                 onChange={e => setGiltAb(e.target.value)} />
        </label>
        <button className="pf-btn is-small" disabled={amArbeiten}
                onClick={() => onFreigeben(giltAb)}>
          Freigeben
        </button>
        <button className="pf-linkbtn vt-verwerfen" onClick={onVerwerfen} disabled={amArbeiten}>
          Entwurf verwerfen
        </button>
      </div>

      {pruefErgebnis && (
        pruefErgebnis.in_ordnung
          ? <div className="vt-ok">Rendert in allen Varianten sauber.</div>
          : <div className="vt-fehler">
              <strong>So lässt sich die Fassung nicht freigeben:</strong>
              {pruefErgebnis.fehler.map((f, i) => <div key={i}>{f}</div>)}
            </div>
      )}
      {fehler && <div className="vt-fehler">{fehler}</div>}

      <div className="vt-hint">
        Freigeben friert diese Fassung ein — Vertragstext und AGB zusammen. Verträge, die
        ab dem genannten Tag vorgelegt werden, bekommen sie; bereits geschlossene behalten
        ihre.
      </div>
    </div>
  );
}

